import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';
import type { InviteChannel, InviteStatus } from '@/api/types';

/**
 * One match from /portal/invites/search — cross-inviter duplicate-prospect
 * lookup. Whatever the server returns is what every outreacher can see, so
 * keep this lean (no notes, no financial fields).
 */
export interface ProspectMatch {
  id: number;
  publicId: string;
  channel: InviteChannel;
  category: string;
  targetIdentifier: string;
  status: InviteStatus;
  createdAt: string;
  convertedAt: string | null;
  inviterId: number;
  inviterName: string;
  inviterTeam: string;
}

interface UseProspectSearchOptions {
  q: string;
  channel?: InviteChannel;
  exact?: boolean;
  /** Skip the request until this is true — used by LogInvite to wait for normalized handle. */
  enabled?: boolean;
}

export function useProspectSearch({
  q,
  channel,
  exact,
  enabled = true,
}: UseProspectSearchOptions) {
  const api = useApi();
  const trimmed = q.trim();
  return useQuery({
    queryKey: ['prospect-search', trimmed, channel ?? null, exact ? 'exact' : 'fuzzy'],
    queryFn: () =>
      api.get<ProspectMatch[]>('/portal/invites/search', {
        q: trimmed,
        ...(channel ? { channel } : {}),
        ...(exact ? { exact: 'true' } : {}),
      }),
    enabled: enabled && trimmed.length > 0,
    staleTime: 10_000,
  });
}
